/**
 * Unit conversion. CLAUDE.md §16.1.
 *
 * Two families of function, and the split is the safety property:
 *
 *   convert()          — same dimension, pure scale factor, no analyte needed.
 *   convertGlucose() / convertCreatinine() / convertUacr() / convertHba1c()
 *                      — cross mass<->substance or affine boundaries, so the
 *                        analyte is named in the function itself and cannot be
 *                        supplied by accident.
 *
 * There is deliberately no generic `convert(mgPerDl, 'mmol/L')`: that call has
 * no correct answer without knowing whether it is glucose or creatinine.
 *
 * Nothing here rounds. §16.1: "Rounding happens once, at display."
 */
import { fail, ok } from './result.js';
import { quantity } from './quantity.js';
import { ANALYTES, UACR_MG_PER_G_PER_MG_PER_MMOL, UNITS, dimensionOf, } from './ucum.js';
/**
 * Convert within a single dimension by scale factor.
 *
 * Refuses across dimensions, which is what stops a creatinine in mg/dL being
 * handed to a glucose conversion: those are the same dimension, so this
 * function would happily do nothing — hence glucose has its own entry point
 * below and this one only ever applies a dimension-safe factor.
 */
export const convert = (q, to) => {
    const from = q.unit;
    if (from === to)
        return ok({ value: q.value, unit: to });
    if (dimensionOf(from) !== dimensionOf(to)) {
        return fail('INCOMPATIBLE_UNITS', `Cannot convert ${from} to ${to}: different dimensions (${dimensionOf(from)} vs ${dimensionOf(to)}).`, { from, to });
    }
    const canonical = q.value * UNITS[from].toCanonical;
    return quantity(canonical / UNITS[to].toCanonical, to);
};
// ---------------------------------------------------------------- glucose ---
/**
 * Glucose mg/dL <-> mmol/L.
 * CLAUDE.md §2.4 and §16.1: `mmol/L = mg/dL / 18.0182`. The factor is
 * contractual — do not re-derive it from a molar mass (§20.3).
 */
export const convertGlucose = (q, to) => {
    if (q.unit === to)
        return ok({ value: q.value, unit: to });
    const factor = ANALYTES.glucose.mgPerDlPerMmolPerL;
    const value = q.unit === 'mg/dL' ? q.value / factor : q.value * factor;
    return quantity(value, to);
};
export const glucoseToMgDl = (q) => convertGlucose(q, 'mg/dL');
export const glucoseToMmolL = (q) => convertGlucose(q, 'mmol/L');
// ------------------------------------------------------------- creatinine ---
/** Serum creatinine mg/dL <-> µmol/L. 1 mg/dL = 88.4017 µmol/L. */
export const convertCreatinine = (q, to) => {
    if (q.unit === to)
        return ok({ value: q.value, unit: to });
    // mmol/L per mg/dL = 1 / 1131.2, so µmol/L per mg/dL = 1000 / 1131.2 = 88.4017.
    const umolPerMgDl = 1000 / ANALYTES.creatinine.mgPerDlPerMmolPerL;
    const value = q.unit === 'mg/dL' ? q.value * umolPerMgDl : q.value / umolPerMgDl;
    return quantity(value, to);
};
export const creatinineToMgDl = (q) => convertCreatinine(q, 'mg/dL');
// ------------------------------------------------------------------- UACR ---
/**
 * Urine albumin-to-creatinine ratio, mg/g <-> mg/mmol.
 * 1 mmol creatinine = 113.12 mg = 0.11312 g, so 1 mg/mmol = 8.8402 mg/g.
 * Source: KDIGO 2012 CKD guideline ACR conversion.
 */
export const convertUacr = (q, to) => {
    if (q.unit === to)
        return ok({ value: q.value, unit: to });
    const value = q.unit === 'mg/mmol'
        ? q.value * UACR_MG_PER_G_PER_MG_PER_MMOL
        : q.value / UACR_MG_PER_G_PER_MG_PER_MMOL;
    return quantity(value, to);
};
export const uacrToMgPerG = (q) => convertUacr(q, 'mg/g');
// ------------------------------------------------------------------ HbA1c ---
/**
 * HbA1c NGSP (%) <-> IFCC (mmol/mol).
 * CLAUDE.md §2.4: `IFCC = (NGSP − 2.15) × 10.929`.
 *
 * This relation is affine, not a scale factor, which is why HbA1c cannot go
 * through `convert()`. §16.1 also requires both forms to be stored and neither
 * inferred silently — this function is how the second one is *derived on
 * purpose*, at the point where that is the caller's intent.
 */
export const NGSP_IFCC_INTERCEPT = 2.15;
export const NGSP_IFCC_SLOPE = 10.929;
export const convertHba1c = (q, to) => {
    if (q.unit === to)
        return ok({ value: q.value, unit: to });
    const value = q.unit === '%'
        ? (q.value - NGSP_IFCC_INTERCEPT) * NGSP_IFCC_SLOPE
        : q.value / NGSP_IFCC_SLOPE + NGSP_IFCC_INTERCEPT;
    if (value < 0) {
        return fail('OUT_OF_RANGE', `HbA1c ${q.value} ${q.unit} converts to a negative ${to} value. NGSP below ` +
            `${NGSP_IFCC_INTERCEPT}% is outside the domain of the IFCC relation.`, { from: q.unit, to, value: q.value });
    }
    return quantity(value, to);
};
export const hba1cToNgsp = (q) => convertHba1c(q, '%');
export const hba1cToIfcc = (q) => convertHba1c(q, 'mmol/mol');
//# sourceMappingURL=convert.js.map